// src/ApiService/baseService.tsx

import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { LOCAL_STORAGE } from "../Constant/constant";
import { API_URL } from "../Constant/Environment";
import Storage from "../Helper/localStorageHelper";
import userServiceObj from "./userService";
import { AuthToken, CustomError } from "Interface/common";
import { navigateTo } from "Helper/navigationHelper";

class BaseApiService {
  API_URL: string;
  baseURL: string;

  private static interceptorsSetup = false;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.API_URL = API_URL;

    // Only setup interceptors once globally
    if (!BaseApiService.interceptorsSetup) {
      this.setupAuthInterceptor();
      this.setupResponseInterceptor();
      BaseApiService.interceptorsSetup = true;
    }
  }

  setupAuthInterceptor() {
    // Ensure axios always sends cookies (for Set-Cookie support)
    axios.defaults.withCredentials = true;

    axios.interceptors.request.use(
      async (config) => {
        const authTokenString = Storage.getData(LOCAL_STORAGE.TOKEN);

        if (authTokenString) {
          const authToken: AuthToken = authTokenString as unknown as AuthToken;
          if (config.headers) {
            config.headers["ms_login"] = false;
            config.headers.Authorization = `Bearer ${authToken}`;
          }
        }
        return config;
      },
      (error: AxiosError) => {
        console.log(error);
        return Promise.reject(error);
      }
    );
  }

  private setupResponseInterceptor() {
    axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (err: AxiosError | any) => {
        const error = {
          message: err.response?.data?.message,
          msg: err.response?.data?.message,
          statusCode: err?.status,
          code: err?.status,
          data: err.response?.data,
        } as CustomError;

        const originalRequest: InternalAxiosRequestConfig & {
          _retry?: boolean;
        } = err.config;

        // 🔒 Avoid infinite loop
        if (originalRequest?._retry) {
          return Promise.reject(error);
        }

        if (err?.response?.status === 401) {
          try {
            // Mark request as retried
            originalRequest._retry = true;

            // 🚫 Skip refresh loop: if it's the refresh endpoint itself, log out
            if (
              originalRequest.url?.includes("/refresh-token") ||
              originalRequest.url?.includes("/login")
            ) {
              Storage.removeAllData();
              navigateTo("/login");
              return Promise.reject(error);
            }

            const { data }: AxiosResponse = await userServiceObj.refreshToken(
              {}
            );

            if (data?.data?.accessToken) {
              Storage.setData(LOCAL_STORAGE.TOKEN, data.data.accessToken);

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`;
              }
              return axios(originalRequest);
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (refreshError: any) {
            console.error("Refresh token failed:", refreshError);

            Storage.removeAllData();
            navigateTo("/login");
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  post(
    route = "",
    postData = {},
    isBase: boolean = false,
    signal?: AbortSignal | undefined,
    customHeaders?: object
  ) {
    return axios.post(
      `${this.API_URL}${!isBase ? this.baseURL + "/" : ""}${route}`,
      postData,
      { signal: signal, headers: { ...(customHeaders || {}) } }
    );
  }

  delete(route = "", postData = {}, isBase: boolean = false) {
    return axios.delete(
      `${this.API_URL}${!isBase ? this.baseURL + "/" : ""}${route}`,
      postData
    );
  }

  postFileUpload(
    route = "",
    postData = {},
    signal?: AbortSignal | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onUploadProgress?: any,
    isBase: boolean = false
  ) {
    const config = {
      signal: signal,
      onUploadProgress: onUploadProgress,
    };
    return axios.post(
      `${this.API_URL}${!isBase ? this.baseURL + "/" : ""}${route}`,
      postData,
      config
    );
  }

  put(route = "", putData = {}) {
    return axios.put(`${this.API_URL}${this.baseURL}/${route}`, putData);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(route: string, params: any) {
    let url = `${this.API_URL}${this.baseURL}/`;
    if (route === "") {
      url = `${this.API_URL}${this.baseURL}`;
    }
    return axios.get(`${url}${route}`, { params });
  }

  axiosInstance() {
    return axios;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError(error: any) {
    let errorMessage =
      "An error occurred while processing your request. Please try again later.";

    switch (
      error.response ? error.response.status : error.code ? error.code : null
    ) {
      case 0:
        errorMessage =
          "Unable to connect to the server. Please check your internet connection and try again later.";
        break;
      case 400:
        errorMessage = `Error: ${error.response.data.error.message}. Please try again later.`;
        break;
      case 401:
        errorMessage =
          "Unauthorized access. Please log in and try again later.";
        break;
      case 403:
        errorMessage = `Error: ${error.response.data.error.message}.`;
        break;
      case 504:
        errorMessage =
          "We are sorry, but we could not get a timely response from the server. This might be due to a temporary issue, high server load, or connectivity problems. Refresh the page and try again. If the issue persists, contact support team for assistance.";
        break;
      case 500:
        errorMessage =
          "We apologize, but our server encountered an internal error while processing your request. Refresh the page and try again. If the problem persists, contact support team for assistance.";
        break;
      case "ERR_NETWORK":
        errorMessage =
          "We apologize, but our server encountered an internal error while processing your request. Refresh the page and try again. If the problem persists, contact support team for assistance.";
        break;
      default:
        errorMessage = `Backend returned code ${
          error.response ? error.response.status : "unknown"
        }, body was: ${
          error.response ? JSON.stringify(error.response.data) : "unknown"
        }`;
    }
    console.log(errorMessage);
    return Promise.reject({
      message: errorMessage,
      code: error?.response?.status ? error?.response?.status : error?.code,
      msg: error?.response?.data?.message,
    });
  }
}

export default BaseApiService;
